import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Cell,
} from 'recharts';
import { chartColors } from './chart-theme';

interface DistributionChartProps {
  data: Array<{ label: string; count: number }>;
  height?: number;
  emptyHint?: string;
  layout?: 'horizontal' | 'vertical';
}

export function DistributionChart({
  data,
  height = 160,
  emptyHint,
  layout = 'horizontal',
}: DistributionChartProps) {
  if (data.length === 0 || data.every((d) => d.count === 0)) {
    return (
      <div className="text-sm text-muted-foreground">
        {emptyHint ?? 'No data yet'}
      </div>
    );
  }
  const colors = chartColors();
  // Our `layout` prop describes bar orientation. Recharts' `layout` prop
  // describes the CATEGORY AXIS — they are inverted. Keep the mapping
  // explicit so this doesn't get flipped accidentally.
  const horizontalBars = layout === 'horizontal';
  return (
    <ResponsiveContainer
      width="100%"
      height={height}
    >
      <BarChart
        data={data}
        layout={horizontalBars ? 'vertical' : 'horizontal'}
        margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
      >
        <CartesianGrid
          stroke={colors.border}
          strokeDasharray="3 3"
          horizontal={!horizontalBars}
          vertical={horizontalBars}
        />
        {horizontalBars ? (
          <>
            <XAxis
              type="number"
              stroke={colors.mutedForeground}
              fontSize={11}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <YAxis
              type="category"
              dataKey="label"
              stroke={colors.mutedForeground}
              fontSize={11}
              tickLine={false}
              axisLine={false}
              width={64}
            />
          </>
        ) : (
          <>
            <XAxis
              type="category"
              dataKey="label"
              stroke={colors.mutedForeground}
              fontSize={11}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              type="number"
              stroke={colors.mutedForeground}
              fontSize={11}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
              width={32}
            />
          </>
        )}
        <Tooltip
          contentStyle={{
            backgroundColor: colors.card,
            border: `1px solid ${colors.border}`,
            borderRadius: 8,
            fontSize: 12,
          }}
        />
        <Bar
          dataKey="count"
          radius={[4, 4, 4, 4]}
        >
          {data.map((entry, index) => (
            <Cell
              key={entry.label + index}
              fill={colors.primary}
            />
          ))}
        </Bar>
      </BarChart>
    </ResponsiveContainer>
  );
}
